import { number } from "../../../config";

/**
 * Fixed workflow user-chip variable ids (aligned with backend / form builder).
 * @author Abhishek Dhillon
 */
export const USER_CHIP_FIXED_VARIABLE_IDS = {
  SUBMITTER_NAME: number.TEN,
  USER_ID: number.ELEVEN,
  MOBILE_NUMBER: number.TWELVE,
  EMAIL: number.THIRTEEN,
  DOMAIN_EMAIL: number.FOURTEEN,
  DEPARTMENT: number.THREE,
  JOB_ROLE: number.SIX,
  MANAGER: number.SEVEN,
  OFFICE_LOCATION: number.EIGHT,
  L1_MANAGER: number.FIFTEEN,
  L2_MANAGER: number.SIXTEEN,
};

/**
 * Display strings for user chip preview / read-only paths.
 * @author Abhishek Dhillon
 */
export const USER_CHIP_LABELS = {
  UNKNOWN_USER: "Unknown User",
  NO_VALUE_SELECTED: "No Value Selected",
};

/**
 * Finds the matching option for a stored value.
 * @param {*} value - Stored option value
 * @param {Array} options - Option list with value/label
 * @returns {{ label: string, value: string }}
 * @author Abhishek Dhillon
 */
export const findUserChipSelectedOption = (value, options) => {
  const selectedOption = options?.find((option) => option?.value === value);
  return selectedOption || { label: "", value: "" };
};

/**
 * Wraps a scalar in label/value shape for display.
 * @param {*} value - Raw value
 * @returns {{ label: string, value: string }|string} Object when truthy, empty string otherwise
 * @author Abhishek Dhillon
 */
export const createUserChipSimpleValue = (value) => (value ? { label: value, value: value } : "");

/**
 * Builds per-fixed-variable resolvers for user chip display (current user vs submitter).
 * @param {Object} params
 * @param {boolean} params.isFormSubmitted - True when viewing a submitted form with submitDetails
 * @param {Object} params.submitDetails - Submitter snapshot when submitted
 * @param {Object} params.user - Logged-in user from auth
 * @param {Object} params.userChip - WorkInformation chip row (options, value, type)
 * @param {*} params.department - User.Department fallback for dropdown chips
 * @param {*} params.jobRole - User.JobRole fallback
 * @param {*} params.manager - User.Manager fallback
 * @param {*} params.officeLocation - User.OfficeLocation fallback
 * @returns {Object<number, Function>} Map of fixedVariableId -> () => display value
 * @author Abhishek Dhillon
 */
export const buildUserChipVariableHandlers = ({
  isFormSubmitted,
  submitDetails,
  user,
  userChip,
  department,
  jobRole,
  manager,
  officeLocation,
}) => {
  const ids = USER_CHIP_FIXED_VARIABLE_IDS;

  return {
    [ids.SUBMITTER_NAME]: () => ({
      label: isFormSubmitted ? submitDetails?.name : user?.Name || USER_CHIP_LABELS.UNKNOWN_USER,
      value: isFormSubmitted ? submitDetails?.id : user?.id || USER_CHIP_LABELS.UNKNOWN_USER,
    }),

    [ids.USER_ID]: () => createUserChipSimpleValue(isFormSubmitted ? submitDetails?.id : user?.UserId),
    [ids.MOBILE_NUMBER]: () => createUserChipSimpleValue(isFormSubmitted ? submitDetails?.mobileNo : user?.MobileNo),
    [ids.EMAIL]: () => createUserChipSimpleValue(isFormSubmitted ? submitDetails?.email : user?.email),
    [ids.DOMAIN_EMAIL]: () => createUserChipSimpleValue(isFormSubmitted ? submitDetails?.userDomainEmail : user?.userDomainEmail),
    [ids.L1_MANAGER]: () => createUserChipSimpleValue(isFormSubmitted ? submitDetails?.l1ManagerName : user?.L1ManagerName),
    [ids.L2_MANAGER]: () => createUserChipSimpleValue(isFormSubmitted ? submitDetails?.l2ManagerName : user?.L2ManagerName),

    [ids.DEPARTMENT]: () =>
      findUserChipSelectedOption(isFormSubmitted ? userChip?.value : (userChip?.value ?? department), userChip?.options),
    [ids.JOB_ROLE]: () =>
      findUserChipSelectedOption(isFormSubmitted ? userChip?.value : (userChip?.value ?? jobRole), userChip?.options),
    [ids.MANAGER]: () =>
      findUserChipSelectedOption(isFormSubmitted ? userChip?.value : (userChip?.value ?? manager), userChip?.options),
    [ids.OFFICE_LOCATION]: () =>
      findUserChipSelectedOption(isFormSubmitted ? userChip?.value : (userChip?.value ?? officeLocation), userChip?.options),
  };
};

/**
 * Resolves label/value for a user chip element (preview, submitted form, etc.).
 * @param {Object} params
 * @param {Object} params.element - Form element (value may already be object)
 * @param {string} params.elementType - Element type from schema
 * @param {number} [params.fixedVariableId] - Fixed chip id when applicable
 * @param {Object} [params.userChip] - Matched work info row
 * @param {Object} params.variableHandlers - Output of {@link buildUserChipVariableHandlers}
 * @returns {{ label: string, value: string }|Object|string}
 * @author Abhishek Dhillon
 */
export const resolveUserChipElementValue = ({ element, elementType, fixedVariableId, userChip, variableHandlers }) => {
  if (typeof element?.value === "object" && element?.value !== null) {
    return element.value;
  }

  if (variableHandlers?.[fixedVariableId]) {
    return variableHandlers[fixedVariableId]();
  }

  if ((elementType === "dropdown" || userChip?.type === "dropdown") && userChip?.options?.length > 0) {
    return findUserChipSelectedOption(userChip?.value, userChip?.options);
  }

  if (userChip?.value) {
    return createUserChipSimpleValue(userChip?.value);
  }

  return { label: "", value: "" };
};

/**
 * True when result is a non-empty label/value object (for persisting via onChange).
 * @param {*} result - Resolved chip value
 * @returns {boolean}
 * @author Abhishek Dhillon
 */
export const isValidUserChipResult = (result) => result && typeof result === "object" && result?.label && result?.value;

/**
 * Context shared when resolving profile work-information rows or form user chips.
 * @typedef {Object} UserChipResolutionContext
 * @property {boolean} isFormSubmitted - Submitted form view with submitter snapshot
 * @property {Object} [submitDetails] - Submitter fields when submitted
 * @property {Object} [user] - Auth user
 * @property {*} [department] - User.Department fallback for dropdown chips
 * @property {*} [jobRole] - User.JobRole fallback
 * @property {*} [manager] - User.Manager fallback
 * @property {*} [officeLocation] - User.OfficeLocation fallback
 */

/**
 * Resolves label/value for one work-information row from getUserProfileControlsData (same logic as UserChipRenderer).
 * @param {Object} workInformation - API row (_id, value, options, type, fixedVariableId, …)
 * @param {UserChipResolutionContext} context - Current user / submitter context
 * @returns {{ label: string, value: string }|Object|string}
 * @author Abhishek Dhillon
 */
export const resolveWorkInformationDisplayValue = (workInformation, context) => {
  const { isFormSubmitted, submitDetails, user, department, jobRole, manager, officeLocation } = context;
  const variableHandlers = buildUserChipVariableHandlers({
    isFormSubmitted,
    submitDetails,
    user,
    userChip: workInformation,
    department,
    jobRole,
    manager,
    officeLocation,
  });
  const fixedVariableId = workInformation?.fixedVariableId;
  return resolveUserChipElementValue({
    element: { value: workInformation?.value },
    elementType: workInformation?.type,
    fixedVariableId,
    userChip: workInformation,
    variableHandlers,
  });
};
